import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Activity, Clock, CheckCircle, XCircle, AlertCircle, 
  Filter, Calendar, Search, Download, RefreshCw,
  TrendingUp, TrendingDown, Minus, BarChart3,
  MessageSquare, Users, Zap, ChevronDown, ChevronUp
} from 'lucide-react';
import dbService from '../services/dbService';

interface LogEntry {
  id: string;
  timestamp: string;
  type: 'flow_execution' | 'message_sent' | 'message_received' | 'error' | 'warning' | 'info';
  flowId?: string;
  flowName?: string;
  userId?: string;
  status: 'success' | 'error' | 'warning' | 'info';
  message: string;
  details?: any;
}

interface Stats {
  total: number;
  success: number;
  errors: number;
  warnings: number;
  todayCount: number;
  yesterdayCount: number;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
}

const ActivityLog: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('today');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    success: 0,
    errors: 0,
    warnings: 0,
    todayCount: 0,
    yesterdayCount: 0,
    trend: 'stable',
    trendPercentage: 0
  });

  useEffect(() => {
    // Genera alcuni log di esempio al primo caricamento se non ce ne sono
    const existingLogs = dbService.getLogs();
    if (existingLogs.length < 5) {
      generateSampleLogs();
    }
    
    loadLogs();
    const interval = setInterval(loadLogs, 5000); // Aggiorna ogni 5 secondi
    return () => clearInterval(interval);
  }, []);
  
  const generateSampleLogs = () => {
    const sampleLogs: Array<{
      flowId?: string;
      level: 'info' | 'warning' | 'error';
      message: string;
      details?: any;
    }> = [
      {
        flowId: 'flow_welcome',
        level: 'info' as const,
        message: 'Flow di benvenuto eseguito con successo',
        details: { triggeredBy: 'message_received', nodesExecuted: 4, duration: '1.2s' }
      },
      {
        flowId: 'flow_support',
        level: 'info' as const, 
        message: 'Messaggio di supporto inviato',
        details: { recipient: '+393331234567', templateUsed: 'support_response' }
      },
      {
        flowId: 'flow_broadcast',
        level: 'warning' as const,
        message: 'Broadcast newsletter - limite quasi raggiunto',
        details: { sent: 245, limit: 250, remainingQuota: 5 }
      },
      {
        flowId: 'flow_reminder',
        level: 'error' as const,
        message: 'Errore invio promemoria appuntamento',
        details: { error: 'Numero non valido', recipient: '+39333invalid' }
      },
      {
        level: 'info' as const,
        message: 'Sistema WhatsApp connesso',
        details: { apiType: 'cloud', phoneNumber: '+393334445555' }
      }
    ];
    
    sampleLogs.forEach((log, index) => {
      setTimeout(() => {
        dbService.createLog(log);
      }, index * 100);
    });
  };

  const loadLogs = async () => {
    try {
      const allLogs = dbService.getLogs();
      
      // Converti i log dal formato del database
      const formattedLogs: LogEntry[] = allLogs.map((log: any) => ({
        id: log.id,
        timestamp: log.timestamp,
        type: log.type || (log.flowId ? 'flow_execution' : 'info'),
        flowId: log.flowId,
        flowName: log.flowName || (log.flowId ? `Flow ${log.flowId.replace('flow_', '')}` : undefined),
        userId: log.userId || log.details?.contactId,
        status: log.status || log.level || 'info',
        message: log.message,
        details: log.details
      }));

      setLogs(formattedLogs.reverse()); // Mostra i più recenti prima
      calculateStats(formattedLogs);
      setLoading(false);
    } catch (error) {
      console.error('Errore nel caricamento dei log:', error);
      setLoading(false);
    }
  };

  const calculateStats = (logs: LogEntry[]) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const todayLogs = logs.filter(log => log.timestamp.startsWith(today));
    const yesterdayLogs = logs.filter(log => log.timestamp.startsWith(yesterday));

    const successCount = logs.filter(log => log.status === 'success').length;
    const errorCount = logs.filter(log => log.status === 'error').length;
    const warningCount = logs.filter(log => log.status === 'warning').length;

    const trend = todayLogs.length > yesterdayLogs.length ? 'up' : 
                  todayLogs.length < yesterdayLogs.length ? 'down' : 'stable';
    
    const trendPercentage = yesterdayLogs.length > 0 
      ? Math.round(((todayLogs.length - yesterdayLogs.length) / yesterdayLogs.length) * 100)
      : 0;

    setStats({
      total: logs.length,
      success: successCount,
      errors: errorCount,
      warnings: warningCount,
      todayCount: todayLogs.length,
      yesterdayCount: yesterdayLogs.length,
      trend,
      trendPercentage
    });
  };

  const filteredLogs = useMemo(() => {
    let filtered = [...logs];

    // Filtro per tipo
    if (filterType !== 'all') {
      filtered = filtered.filter(log => log.type === filterType);
    }

    // Filtro per stato
    if (filterStatus !== 'all') {
      filtered = filtered.filter(log => log.status === filterStatus);
    }

    // Filtro per data
    const now = new Date();
    switch (dateRange) {
      case 'today':
        filtered = filtered.filter(log => 
          log.timestamp.startsWith(now.toISOString().split('T')[0])
        );
        break;
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(log => 
          new Date(log.timestamp) >= weekAgo
        );
        break;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(log => 
          new Date(log.timestamp) >= monthAgo
        );
        break;
    }

    // Filtro per ricerca
    if (searchQuery) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.flowName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.userId?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [logs, filterType, filterStatus, dateRange, searchQuery]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Activity className="w-4 h-4 text-blue-500" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'flow_execution':
        return <Zap className="w-4 h-4" />;
      case 'message_sent':
      case 'message_received':
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} minuti fa`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} ore fa`;
    
    return date.toLocaleString('it-IT', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportLogs = () => {
    const data = JSON.stringify(filteredLogs, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-log-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearLogs = () => {
    if (window.confirm('Sei sicuro di voler cancellare tutti i log?')) {
      // In un'app reale, useresti dbService.clearLogs()
      setLogs([]);
      calculateStats([]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-whatsapp-green" />
          <p className="text-gray-600 dark:text-gray-400">Caricamento attività...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Registro Attività
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Monitora tutte le attività e le esecuzioni del sistema
          </p>
        </div>

        {/* Statistiche */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="w-8 h-8 text-blue-500" />
              <span className={`flex items-center gap-1 text-sm font-medium ${
                stats.trend === 'up' ? 'text-green-600' : 
                stats.trend === 'down' ? 'text-red-600' : 
                'text-gray-600'
              }`}>
                {stats.trend === 'up' && <TrendingUp className="w-4 h-4" />}
                {stats.trend === 'down' && <TrendingDown className="w-4 h-4" />}
                {stats.trend === 'stable' && <Minus className="w-4 h-4" />}
                {Math.abs(stats.trendPercentage)}%
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.todayCount}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">Attività oggi</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.success}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">Completate</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.errors}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">Errori</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.warnings}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">Avvisi</p>
          </motion.div>
        </div>

        {/* Filtri e Azioni */}
        <div className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Barra di ricerca */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca attività..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                />
              </div>
            </div>

            {/* Filtri */}
            <div className="flex gap-4">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
              >
                <option value="all">Tutti i tipi</option>
                <option value="flow_execution">Esecuzioni Flow</option>
                <option value="message_sent">Messaggi Inviati</option>
                <option value="message_received">Messaggi Ricevuti</option>
                <option value="error">Errori</option>
                <option value="info">Info</option>
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
              >
                <option value="all">Tutti gli stati</option>
                <option value="success">Successo</option>
                <option value="error">Errore</option>
                <option value="warning">Avviso</option>
                <option value="info">Info</option>
              </select>

              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-2 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
              >
                <option value="today">Oggi</option>
                <option value="week">Ultima settimana</option>
                <option value="month">Ultimo mese</option>
                <option value="all">Tutto</option>
              </select>
            </div>

            {/* Azioni */}
            <div className="flex gap-2">
              <button
                onClick={loadLogs}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors"
                title="Aggiorna"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={exportLogs}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors"
                title="Esporta"
              >
                <Download className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Lista Attività */}
        <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-dark-border">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Attività Recenti
              <span className="text-sm font-normal text-gray-500">
                ({filteredLogs.length} risultati)
              </span>
            </h2>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-dark-border">
            <AnimatePresence>
              {filteredLogs.length === 0 ? (
                <div className="p-12 text-center">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Nessuna attività trovata
                  </p>
                </div>
              ) : (
                filteredLogs.map((log, index) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icona Stato */}
                      <div className="mt-1">
                        {getStatusIcon(log.status)}
                      </div>

                      {/* Contenuto */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getTypeIcon(log.type)}
                          <span className="font-medium text-gray-900 dark:text-white">
                            {log.message}
                          </span>
                          {log.flowName && (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              • {log.flowName}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimestamp(log.timestamp)}
                          </span>
                          {log.userId && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {log.userId}
                            </span>
                          )}
                        </div>

                        {/* Dettagli espandibili */}
                        {log.details && (
                          <button
                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                            className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          >
                            {expandedLog === log.id ? (
                              <>
                                <ChevronUp className="w-4 h-4" />
                                Nascondi dettagli
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4" />
                                Mostra dettagli
                              </>
                            )}
                          </button>
                        )}

                        {expandedLog === log.id && log.details && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-3 p-3 bg-gray-50 dark:bg-dark-bg rounded-lg"
                          >
                            <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          {/* Footer con paginazione (se necessario) */}
          {filteredLogs.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-dark-border text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Mostrando {filteredLogs.length} di {logs.length} attività totali
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
